import { getEnv } from './env';

// Thrown for any non-2xx response.
// `session` carries the rotated Cognito session on WRONG_CODE 401 so the caller
// can retry without re-issuing the OTP; `retryAfterSeconds` drives lockout UIs.
export class ApiError extends Error {
  public retryAfterSeconds?: number;
  public session?: string;
  public details?: Record<string, unknown>;

  constructor(
    public status: number,
    public code: string,
    message: string,
    retryAfterSeconds?: number,
    session?: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
    this.retryAfterSeconds = retryAfterSeconds;
    this.session = session;
    this.details = details;
  }
}

type RequestOpts = {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
};

// Error body emitted by the auth.smndiaye backend on non-2xx responses.
// `retry_after_seconds` mirrors the Retry-After header so the lockout countdown
// works even when CORS does not expose that header (it is not safelisted by default).
type ApiErrorBody = {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
  session?: string;
  retry_after_seconds?: number;
};

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// Core fetch wrapper. Uses credentials:'include' so the __Host-session cookie
// set by /auth/verify-otp is both persisted and sent on subsequent requests.
export async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const { authBaseUrl } = getEnv();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const res = await fetch(`${authBaseUrl}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  const text = await res.text();
  const data = text ? safeJson(text) : null;

  if (!res.ok) {
    const errBody = data as ApiErrorBody | null;
    const err = errBody?.error;

    const retryAfterHeader = res.headers.get('Retry-After');
    const retryAfterFromHeader = retryAfterHeader ? parseInt(retryAfterHeader, 10) : NaN;
    const retryAfterFromBody =
      typeof errBody?.retry_after_seconds === 'number' ? errBody.retry_after_seconds : NaN;
    const retryAfterSeconds = Number.isFinite(retryAfterFromHeader)
      ? retryAfterFromHeader
      : Number.isFinite(retryAfterFromBody)
        ? retryAfterFromBody
        : undefined;

    throw new ApiError(
      res.status,
      err?.code ?? 'UNKNOWN',
      err?.message ?? `HTTP ${res.status}`,
      retryAfterSeconds,
      typeof errBody?.session === 'string' ? errBody.session : undefined,
      err?.details,
    );
  }

  return data as T;
}
